import { useMemo, useRef, useState, type FormEvent } from 'react';

import type { ExtractCandidate } from '@lexio/shared';

import { ApiError, captureExtract, previewExtract } from '../lib/api';

type Pair = 'cs' | 'en';

/** Drzet v souladu se serverem (routes/extract.ts). */
const MAX_TEXT = 200_000;

/** "Zbyva" ve slovech (MyMemory) nebo v castech textu (AI). */
function remainingLabel(mode: 'ai' | 'basic', n: number): string {
  if (mode === 'basic') return `${n} slov`;
  if (n === 1) return '1 část textu';
  return n >= 2 && n <= 4 ? `${n} části textu` : `${n} částí textu`;
}

/**
 * Paste a whole article: mine content words, skip ones already in the deck,
 * keep the source sentence as the example.
 */
export function ArticleExtract() {
  const [from, setFrom] = useState<Pair>('en');
  const [to, setTo] = useState<Pair>('cs');
  const [text, setText] = useState('');
  const [items, setItems] = useState<ExtractCandidate[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [scanned, setScanned] = useState(0);
  const [alreadyHave, setAlreadyHave] = useState(0);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(0);
  /** `ai` = posun i "zbyva" se pocita v castech textu, `basic` = ve slovech. */
  const [mode, setMode] = useState<'ai' | 'basic'>('basic');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Prubeh hromadneho pridani; `resumeAt` = odkud pokracovat po preruseni. */
  const [bulk, setBulk] = useState<{ added: number; skipped: number; running: boolean; resumeAt: number | null } | null>(
    null,
  );
  const stopRef = useRef(false);

  const selectedItems = useMemo(
    () => items.filter((_, i) => selected.has(i)),
    [items, selected],
  );

  const runPreview = async (e?: FormEvent, offset = 0) => {
    e?.preventDefault();
    const article = text.trim();
    if (article.length < 20) {
      setError('Vlož delší text — aspoň pár vět.');
      return;
    }
    if (article.length > MAX_TEXT) {
      setError(`Text je moc dlouhý (${article.length.toLocaleString('cs')} znaků, max ${MAX_TEXT.toLocaleString('cs')}). Vlož ho po částech.`);
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    setItems([]);
    setSelected(new Set());
    try {
      const res = await previewExtract({ text: article, sourceLang: from, targetLang: to, offset });
      setScanned(res.scanned);
      setAlreadyHave(res.alreadyHave);
      setNextOffset(res.nextOffset);
      setRemaining(res.remaining);
      setMode(res.mode);
      setItems(res.items);
      setSelected(new Set(res.items.map((_, i) => i)));
      if (res.items.length === 0 && res.nextOffset !== null) {
        setInfo('V této dávce se nic nepřeložilo — zkus další.');
      } else if (res.items.length === 0 && res.alreadyHave > 0) {
        setInfo(`Všechna vytěžená slova (${res.alreadyHave}) už ve slovníku máš.`);
      } else if (res.items.length === 0) {
        setInfo('V článku se nenašla žádná nová slovíčka.');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Vytěžení se nepodařilo');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Prida vsechna nova slova z textu, davku po davce. Vety se neprekladaji -
   * setri to denni limit prekladace i cas; anglicka veta zustane jako priklad.
   *
   * Pridana slova z dalsi davky "zmizi" (uz je mas), takze se posun snizuje
   * o pocet pridanych - jinak by se jejich mista preskocila.
   */
  const addAll = async (startOffset = 0) => {
    const article = text.trim();
    if (article.length < 20 || article.length > MAX_TEXT) return;
    stopRef.current = false;
    setError(null);
    setInfo(null);
    setItems([]);
    setSelected(new Set());
    setNextOffset(null);
    let offset = startOffset;
    // Pokracovani po preruseni navazuje na dosavadni pocty.
    const resume = startOffset > 0 && bulk !== null;
    let added = resume ? bulk.added : 0;
    let skipped = resume ? bulk.skipped : 0;
    setBulk({ added, skipped, running: true, resumeAt: null });

    try {
      for (;;) {
        if (stopRef.current) {
          setBulk({ added, skipped, running: false, resumeAt: offset });
          setInfo('Zastaveno. Pokračovat můžeš tlačítkem níž.');
          return;
        }
        const res = await previewExtract({ text: article, sourceLang: from, targetLang: to, offset, translateContext: false });
        setScanned(res.scanned);
        setAlreadyHave(res.alreadyHave);
        setRemaining(res.remaining);
        setMode(res.mode);

        let addedNow = 0;
        if (res.items.length > 0) {
          const cap = await captureExtract({
            sourceLang: from,
            targetLang: to,
            items: res.items.map((item) => ({
              sourceTerm: item.sourceTerm,
              targetTerm: item.targetTerm,
              context: item.context,
              contextTranslated: item.contextTranslated,
              variants: item.variants,
              note: item.note,
              level: item.level,
            })),
          });
          addedNow = cap.added;
          added += cap.added;
        }

        if (res.quotaHit) {
          setBulk({ added, skipped, running: false, resumeAt: res.nextOffset });
          setError('Překladač vyčerpal denní limit. Pokračuj zítra tlačítkem „Pokračovat“ – text tu nech.');
          return;
        }
        if (res.nextOffset === null) break;
        if (res.mode === 'ai') {
          // AI jde po castech textu - pridana slova cislovani casti neposunou.
          offset = res.nextOffset;
        } else {
          const attempted = res.nextOffset - offset;
          skipped += attempted - res.items.length;
          offset = res.nextOffset - addedNow;
        }
        setBulk({ added, skipped, running: true, resumeAt: null });
      }
      setBulk({ added, skipped, running: false, resumeAt: null });
      setInfo(
        `Hotovo: přidáno ${added} slov${skipped ? `, ${skipped} se nepodařilo přeložit` : ''}. ` +
          'Učíš je z balíčku Moje zachycené.',
      );
    } catch (err) {
      setBulk({ added, skipped, running: false, resumeAt: offset });
      setError(err instanceof ApiError ? err.message : 'Hromadné přidání se přerušilo');
    }
  };

  const toggle = (i: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const save = async () => {
    if (selectedItems.length === 0) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const res = await captureExtract({
        sourceLang: from,
        targetLang: to,
        items: selectedItems.map((item) => ({
          sourceTerm: item.sourceTerm,
          targetTerm: item.targetTerm,
          context: item.context,
          contextTranslated: item.contextTranslated,
          variants: item.variants,
          note: item.note,
          level: item.level,
        })),
      });
      const parts = [`Přidáno ${res.added}`];
      if (res.existed) parts.push(`už tam bylo ${res.existed}`);
      if (res.failed) parts.push(`nevyšlo ${res.failed}`);
      setInfo(`${parts.join(', ')}. Učíš je z balíčku Moje zachycené — u každého zůstala věta z článku.`);
      // Pridana slova z textu "zmizi" (uz je mas) - posun na dalsi davku se o ne zkrati.
      if (mode === 'basic') setNextOffset((n) => (n === null ? n : Math.max(n - res.added, 0)));
      const kept = items.filter((_, i) => !selected.has(i));
      setItems(kept);
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Uložení se nepodařilo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="extract">
      <p className="muted small">
        Vlož článek nebo titulky (třeba celou epizodu). Lexio vytáhne slovíčka, která ještě nemáš,
        a u každého nechá větu ze zdroje.
      </p>

      <div className="row translate-dir">
        <button
          type="button"
          className={`btn ghost small ${from === 'en' ? 'active' : ''}`}
          onClick={() => {
            setFrom('en');
            setTo('cs');
          }}
        >
          Článek EN
        </button>
        <button
          type="button"
          className={`btn ghost small ${from === 'cs' ? 'active' : ''}`}
          onClick={() => {
            setFrom('cs');
            setTo('en');
          }}
        >
          Článek CS
        </button>
      </div>

      <form onSubmit={(e) => void runPreview(e)} className="translate-form">
        <textarea
          className="input translate-box extract-box"
          rows={10}
          placeholder={
            from === 'en'
              ? 'Paste an English article or a few paragraphs…'
              : 'Vlož český článek nebo pár odstavců…'
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoCapitalize="off"
          autoCorrect="off"
        />
        <button className="btn primary" type="submit" disabled={busy || bulk?.running || text.trim().length < 20}>
          {busy ? 'Těžím slova…' : 'Vytěžit slova (projít a vybrat)'}
        </button>
        {bulk?.running ? (
          <button className="btn" type="button" onClick={() => (stopRef.current = true)}>
            Zastavit
          </button>
        ) : (
          <button
            className="btn"
            type="button"
            disabled={busy || text.trim().length < 20}
            onClick={() => void addAll(bulk?.resumeAt ?? 0)}
          >
            {bulk?.resumeAt != null ? 'Pokračovat v přidávání všech' : 'Přidat všechna nová slova najednou'}
          </button>
        )}
        {bulk && (
          <p className="muted small">
            {bulk.running ? 'Přidávám… ' : ''}Přidáno {bulk.added}
            {bulk.skipped ? `, bez překladu ${bulk.skipped}` : ''}
            {bulk.running && remaining > 0 ? `, zbývá ${remainingLabel(mode, remaining)}` : ''}.
          </p>
        )}
      </form>

      {items.length > 0 && (
        <div className="extract-result">
          <p className="muted small">
            Nových {items.length}
            {scanned > 0 ? ` z ${scanned} vytěžených` : ''}
            {alreadyHave > 0 ? `, ${alreadyHave} už máš` : ''}.
            {remaining > 0
              ? mode === 'ai'
                ? ` Text jde po částech, čeká ještě ${remainingLabel(mode, remaining)}.`
                : ` Po 30 najednou, dalších ${remaining} čeká.`
              : ''}
          </p>
          <div className="extract-toolbar">
            <button type="button" className="btn ghost small" onClick={() => setSelected(new Set(items.map((_, i) => i)))}>
              Vybrat vše
            </button>
            <button type="button" className="btn ghost small" onClick={() => setSelected(new Set())}>
              Nic
            </button>
          </div>
          <ul className="extract-list">
            {items.map((item, i) => (
              <li key={`${item.sourceTerm}-${i}`}>
                <label className="extract-item">
                  <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)} />
                  <span>
                    <span className="extract-pair">
                      <strong>{item.sourceTerm}</strong>
                      <span className="muted"> → {item.targetTerm}</span>
                      {item.level && <span className="tag"> {item.level}</span>}
                    </span>
                    {item.variants && item.variants.length > 0 && (
                      <span className="extract-ex">{item.variants.join(' · ')}</span>
                    )}
                    {item.note && <span className="extract-ex">💡 {item.note}</span>}
                    <span className="extract-ex">{item.context}</span>
                    {item.contextTranslated && (
                      <span className="extract-ex extract-ex-cs">{item.contextTranslated}</span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <button
            className="btn primary"
            type="button"
            onClick={() => void save()}
            disabled={saving || selectedItems.length === 0}
          >
            {saving ? 'Ukládám…' : `Přidat vybrané ke zkoušení (${selectedItems.length})`}
          </button>
        </div>
      )}

      {nextOffset !== null && !busy && (
        <button className="btn" type="button" onClick={() => void runPreview(undefined, nextOffset)}>
          Další slova (zbývá {remainingLabel(mode, remaining)}){items.length > 0 ? ' – nepřidané z téhle dávky zahodí' : ''}
        </button>
      )}

      {error && <p className="error">{error}</p>}
      {info && <p className="muted">{info}</p>}
    </div>
  );
}
